import React, { useState } from 'react';
import {
  X, Timer, Activity, TrendingUp, Minus, Music2, Volume2,
  Sparkles, Wand2, Check, Brain
} from 'lucide-react';
import { EARTH, TYPOGRAPHY, calculateBarsForDuration, calculateDuration } from '../theme/constants.js';
import { GENRE_TEMPLATES } from '../theme/templates.js';

function SmartGeneratorPanel({ isOpen, onClose, onGenerate, currentSections }) {
  const [targetDuration, setTargetDuration] = useState(3.5);
  const [energyCurve, setEnergyCurve] = useState('dynamic'); // 'dynamic' or 'steady'
  const [genre, setGenre] = useState('Pop');
  const [tempo, setTempo] = useState(120);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState(null);

  const genres = Object.keys(GENRE_TEMPLATES);

  // Calculate estimated bars based on duration and tempo
  const estimatedBars = calculateBarsForDuration(targetDuration, tempo);

  const generateStructure = async () => {
    setIsGenerating(true);

    // Brief pause so the generation reads as considered rather than instant
    await new Promise(r => setTimeout(r, 1500));

    // Find templates matching criteria
    const genreTemplates = GENRE_TEMPLATES[genre] || GENRE_TEMPLATES['Pop'];
    const matchingTemplates = genreTemplates.filter(t => t.energy === energyCurve);
    const baseTemplate = matchingTemplates.length > 0
      ? matchingTemplates[Math.floor(Math.random() * matchingTemplates.length)]
      : genreTemplates[0];

    // Calculate current template duration
    const templateBars = baseTemplate.bars.reduce((sum, b) => sum + b, 0);
    const scaleFactor = estimatedBars / templateBars;

    // Scale section bars to match target duration
    const scaledBars = baseTemplate.bars.map(b => Math.max(4, Math.round(b * scaleFactor)));

    // For dynamic energy, vary the bar counts more
    const finalBars = energyCurve === 'dynamic'
      ? scaledBars.map((b, i) => {
          // Make choruses/drops longer, verses shorter for dynamic feel
          const sectionName = baseTemplate.sections[i].toLowerCase();
          if (sectionName.includes('chorus') || sectionName.includes('drop') || sectionName.includes('climax')) {
            return Math.round(b * 1.2);
          } else if (sectionName.includes('intro') || sectionName.includes('outro')) {
            return Math.round(b * 0.8);
          }
          return b;
        })
      : scaledBars;

    const preview = {
      genre,
      tempo,
      energyCurve,
      sections: baseTemplate.sections,
      bars: finalBars,
      totalBars: finalBars.reduce((sum, b) => sum + b, 0),
      estimatedDuration: calculateDuration(finalBars.reduce((sum, b) => sum + b, 0), tempo),
      basedOn: baseTemplate.name
    };

    setGeneratedPreview(preview);
    setIsGenerating(false);
  };

  const handleApply = () => {
    if (generatedPreview) {
      onGenerate(generatedPreview);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(28, 25, 23, 0.4)' }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundColor: '#faf8f5', border: '1px solid rgba(214, 211, 209, 0.4)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: EARTH.stone[200], background: `linear-gradient(135deg, ${EARTH.terracotta[100]}, ${EARTH.sand[100]})` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${EARTH.terracotta[500]}, ${EARTH.terracotta[600]})` }}>
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="anton-header" style={{ color: EARTH.stone[800], fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '13px' }}>SMART STRUCTURE GENERATOR</h2>
              <p className="text-xs" style={{ color: EARTH.stone[400] }}>Create the perfect arrangement</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/50" style={{ color: EARTH.stone[500] }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Duration Input */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-3" style={{ color: EARTH.stone[700] }}>
              <Timer className="w-4 h-4" style={{ color: EARTH.terracotta[500] }} />
              How long should the song be?
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="10"
                step="0.5"
                value={targetDuration}
                onChange={(e) => setTargetDuration(parseFloat(e.target.value))}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{ backgroundColor: EARTH.stone[200] }}
              />
              <div className="w-20 text-center px-3 py-2 rounded-lg font-semibold" style={{ backgroundColor: EARTH.terracotta[100], color: EARTH.terracotta[700] }}>
                {targetDuration} min
              </div>
            </div>
            <p className="text-xs mt-2" style={{ color: EARTH.stone[400] }}>
              ≈ {estimatedBars} bars at {tempo} BPM
            </p>
          </div>

          {/* Energy Curve */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium mb-3" style={{ color: EARTH.stone[700] }}>
              <Activity className="w-4 h-4" style={{ color: EARTH.terracotta[500] }} />
              Energy curve
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setEnergyCurve('dynamic')}
                className="p-4 rounded-xl text-left transition-all duration-200"
                style={{
                  backgroundColor: energyCurve === 'dynamic' ? EARTH.terracotta[100] : EARTH.stone[50],
                  border: `2px solid ${energyCurve === 'dynamic' ? EARTH.terracotta[400] : EARTH.stone[200]}`
                }}
              >
                <TrendingUp className="w-5 h-5 mb-2" style={{ color: energyCurve === 'dynamic' ? EARTH.terracotta[600] : EARTH.stone[400] }} />
                <div className="font-medium text-sm" style={{ color: EARTH.stone[800] }}>Dynamic</div>
                <div className="text-xs mt-1" style={{ color: EARTH.stone[500] }}>Build-ups, drops, varied intensity</div>
              </button>
              <button
                onClick={() => setEnergyCurve('steady')}
                className="p-4 rounded-xl text-left transition-all duration-200"
                style={{
                  backgroundColor: energyCurve === 'steady' ? EARTH.sage[100] : EARTH.stone[50],
                  border: `2px solid ${energyCurve === 'steady' ? EARTH.sage[400] : EARTH.stone[200]}`
                }}
              >
                <Minus className="w-5 h-5 mb-2" style={{ color: energyCurve === 'steady' ? EARTH.sage[600] : EARTH.stone[400] }} />
                <div className="font-medium text-sm" style={{ color: EARTH.stone[800] }}>Steady</div>
                <div className="text-xs mt-1" style={{ color: EARTH.stone[500] }}>Consistent energy throughout</div>
              </button>
            </div>
          </div>

          {/* Genre & Tempo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: EARTH.stone[700] }}>
                <Music2 className="w-4 h-4" style={{ color: EARTH.terracotta[500] }} />
                Genre
              </label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${EARTH.stone[200]}` }}
              >
                {genres.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: EARTH.stone[700] }}>
                <Volume2 className="w-4 h-4" style={{ color: EARTH.terracotta[500] }} />
                Tempo
              </label>
              <input
                type="number"
                value={tempo}
                onChange={(e) => setTempo(parseInt(e.target.value) || 120)}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${EARTH.stone[200]}` }}
              />
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateStructure}
            disabled={isGenerating}
            className="w-full py-4 rounded-xl font-medium flex items-center justify-center gap-3 transition-all hover:scale-[1.01]"
            style={{ backgroundColor: EARTH.terracotta[500], color: '#fff', boxShadow: '0 4px 12px rgba(201, 89, 58, 0.2)' }}
          >
            {isGenerating ? (
              <>
                <Sparkles className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Generate Structure
              </>
            )}
          </button>

          {/* Preview */}
          {generatedPreview && (
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.sage[50], border: `1px solid ${EARTH.sage[200]}` }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5" style={{ color: EARTH.sage[600] }} />
                  <span className="font-medium" style={{ color: EARTH.sage[800] }}>Generated Structure</span>
                </div>
                <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: EARTH.sage[200], color: EARTH.sage[700] }}>
                  Based on {generatedPreview.basedOn}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {generatedPreview.sections.map((s, i) => (
                  <span key={i} className="px-2 py-1 rounded text-xs" style={{ backgroundColor: EARTH.sage[100], color: EARTH.sage[700] }}>
                    {s} ({generatedPreview.bars[i]}b)
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: EARTH.stone[600] }}>{generatedPreview.totalBars} bars total</span>
                <span className="font-medium" style={{ color: EARTH.sage[700] }}>{generatedPreview.estimatedDuration}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: EARTH.stone[200] }}>
          <button onClick={onClose} className="px-6 py-2 rounded-lg" style={{ color: EARTH.stone[600] }}>Cancel</button>
          <button
            onClick={handleApply}
            disabled={!generatedPreview}
            className="px-6 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: EARTH.sage[600], color: '#fff', boxShadow: '0 2px 8px rgba(92, 112, 73, 0.15)' }}
          >
            Apply to Canvas
          </button>
        </div>
      </div>
    </div>
  );
}

export { SmartGeneratorPanel };
